import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider } from "./context/ThemeContext";
import { MainLayout } from "./components/Layout/MainLayout";
import { TestEnvironmentBadge } from "./components/Layout/TestEnvironmentBadge";
import { ResourcesPage } from "./pages/ResourcesPage.js";
import LoginPage from "./pages/LoginPage";
import PrivateRoute from "./routes/PrivateRoute";
import WorkerDetail from "./pages/WorkerDetail";
import SettingsPage from "./pages/Settings";
import QuestionsPage from "./pages/Question";
import ChatDashboardPage from "./pages/ChatDashboardPage.tsx";
import HourReportingDashboardPage from "./pages/HourReportingDashboardPage.tsx";
import HourReportingDefaultsPage from "./pages/HourReportingDefaultsPage.tsx";
import HourReportingPlacementsPage from "./pages/HourReportingPlacementsPage.tsx";
import HourReportingSandboxPage from "./pages/HourReportingSandboxPage.tsx";
import ProfessionQuestions from "./pages/questions/ProfessionQuestions";
import ProfessionQuestionEditor from "./pages/questions/ProfessionQuestionEditor";
import PersonalQuestionsEditor from "./pages/questions/PersonalQuestionsEditor";

import { SettingsProvider } from "./context/SettingsContext"; // adjust path as needed
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import QueueMonitor from "./pages/QueueMonitor.tsx";
import { AdminApp } from './pages/AdminPage.tsx';
import DebugQueuePage from './pages/DebugQueuePage.tsx';
import AuthCallback from './pages/auth/AuthCallback';
import SilentRenew from './pages/auth/SilentRenew';
import LogoutCallback from './pages/auth/LogoutCallback';
import ReferralLinksPage from './pages/ReferralLinksPage';
import MetaAdAttributionsPage from './pages/MetaAdAttributionsPage';
import ContactLandingPage from './pages/ContactLandingPage';
import PublicSessionFormPage from './components/PublicSessionForm/PublicSessionFormPage';
import SaleslistImportPage from './pages/SaleslistImportPage';
import ImportEasyFlexWorkerPage from './pages/ImportEasyFlexWorkerPage';
import EasyFlexPage from './pages/EasyFlexPage';
import { FeatureFlagRoute } from './routes/FeatureFlagRoute';
import BulkImportPage from './pages/BulkImportPage';
import PendingSessionsPage from './pages/PendingSessionsPage';
import CompanyRequestsList from './pages/company-requests/CompanyRequestsList';
import CompanyRequestDetail from './pages/company-requests/CompanyRequestDetail';
import CompanyRequestRoleDetail from './pages/company-requests/CompanyRequestRoleDetail';
import CertReviewQueuePage from './pages/CertReviewQueuePage';
import DocumentAssessmentPage from './pages/DocumentAssessmentPage';
import RecruitmentReportingPage from './pages/RecruitmentReportingPage';
import { AiObservabilityAccessGuard } from './routes/AiObservabilityAccessGuard';
import { ChatAccessGuard } from './routes/ChatAccessGuard';
import { AiObservabilityLegacyRedirect } from './routes/AiObservabilityLegacyRedirect';
import RoleProtectedRoute from './routes/RoleProtectedRoute';
import { RECRUITMENT_MANAGE_WORKERS_ROLE } from './constants/authRoles';

const WireframePage = React.lazy(() => import('./pages/wireframe/WireframePage'));
const AiDebugPage = React.lazy(() => import('./pages/AiDebugPage'));
const WorkerChatTestPage = React.lazy(() => import('./pages/WorkerChatTestPage'));
const IS_TEST_ENVIRONMENT = import.meta.env.VITE_APP_ENV === 'test';

const RouteLoadingFallback: React.FC = () => (
    <div role="status" aria-live="polite" style={{ padding: '24px' }}>
        Loading page...
    </div>
);

const App: React.FC = () => {
    return (
        <ThemeProvider>
            <FeatureFlagsProvider>
            <TestEnvironmentBadge visible={IS_TEST_ENVIRONMENT} />
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar aria-label="Notifications" />
            <Router>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/silent-renew" element={<SilentRenew />} />
                    <Route path="/auth/logout-callback" element={<LogoutCallback />} />
                    <Route path="/contact/:linkName" element={<ContactLandingPage />} />
                    <Route path="/session-share/:publicShareId" element={<PublicSessionFormPage />} />

                    {/* Protected Routes */}
                    <Route path="/wireframe" element={<PrivateRoute><React.Suspense fallback={<RouteLoadingFallback />}><WireframePage /></React.Suspense></PrivateRoute>} />
                    <Route element={<PrivateRoute><MainLayout><Outlet /></MainLayout></PrivateRoute>}>
                        <Route element={<SettingsProvider><Outlet /></SettingsProvider>}>
                            <Route path="/resources/workers" element={<ResourcesPage />} />
                            <Route path="/resources/workers/bulk-import" element={<BulkImportPage />} />
                            <Route path="/resources/workers/pending-sessions" element={<PendingSessionsPage />} />
                            <Route path="/resources/workers/import/:easyflexWorkerId" element={<ImportEasyFlexWorkerPage />} />
                            <Route path="/resources/easyflex" element={<FeatureFlagRoute flag="easyflexPage" redirectTo="/resources/workers"><EasyFlexPage /></FeatureFlagRoute>} />
                            <Route path="/workers/:workerId" element={<WorkerDetail />} />
                            <Route path="/chat" element={<ChatAccessGuard><ChatDashboardPage /></ChatAccessGuard>} />
                            <Route path="/hours-reporting" element={<HourReportingDashboardPage />} />
                            <Route path="/hours-reporting/placements" element={<HourReportingPlacementsPage />} />
                            <Route path="/hours-reporting/defaults" element={<HourReportingDefaultsPage />} />
                            <Route path="/hours-reporting/test" element={<HourReportingSandboxPage />} />
                            <Route path="/referral-links" element={<ReferralLinksPage />} />
                            <Route path="/referral-links/meta-ads" element={<MetaAdAttributionsPage />} />
                            <Route path="/company-requests" element={<CompanyRequestsList />} />
                            <Route path="/company-requests/:id" element={<CompanyRequestDetail />} />
                            <Route path="/company-requests/:id/roles/:roleId" element={<CompanyRequestRoleDetail />} />
                            <Route path="/saleslist/import" element={<SaleslistImportPage />} />
                            <Route path="/cert-review" element={<CertReviewQueuePage />} />
                            <Route path="/document-assessment" element={<DocumentAssessmentPage />} />
                            <Route path="/unauthorized" element={<Navigate to="/resources/workers" replace />} />
                            <Route element={<RoleProtectedRoute requiredRole={RECRUITMENT_MANAGE_WORKERS_ROLE} />}>
                                <Route path="/reporting/recruitment" element={<RecruitmentReportingPage />} />
                            </Route>
                            
                            {/* Previously Role Protected Routes - Now accessible to all logged-in users */}
                            <Route path="/settings" element={<SettingsPage />} />
                            <Route path="/questions-old" element={<QuestionsPage />} />
                            <Route path="/questions" element={<ProfessionQuestions />} />
                            <Route path="/questions/profession/:professionKey" element={<ProfessionQuestionEditor />} />
                            <Route path="/questions/personal" element={<PersonalQuestionsEditor />} />
                            <Route path="/admin/queues" element={<QueueMonitor />} />
                            <Route path="/admin/debug" element={<FeatureFlagRoute flag="debugTools" redirectTo="/resources/workers"><DebugQueuePage /></FeatureFlagRoute>} />
                            <Route path="/admin/ai-debug" element={<AiObservabilityLegacyRedirect />} />
                            <Route path="/admin/worker-chat" element={
                                // Distinct `key`s: these sibling routes render a guard at the same
                                // Outlet position with a different policy, so without a key React
                                // would reuse the instance (and its 'allowed' state) across an
                                // in-app navigation between them instead of remounting fresh.
                                <AiObservabilityAccessGuard key="worker-chat-access-guard" requireManageSettings>
                                    <React.Suspense fallback={<RouteLoadingFallback />}><WorkerChatTestPage /></React.Suspense>
                                </AiObservabilityAccessGuard>
                            } />
                            <Route path="/admin/ai-observability" element={
                                <AiObservabilityAccessGuard key="ai-observability-access-guard">
                                    <React.Suspense fallback={<RouteLoadingFallback />}><AiDebugPage /></React.Suspense>
                                </AiObservabilityAccessGuard>
                            } />
                            <Route path="/admin/*" element={<AdminApp />} />

                            {/* Default redirect */}
                            <Route path="/" element={<Navigate to="/resources/workers" replace />} />
                        </Route>
                    </Route>

                    {/* Catch-all redirect */}
                    <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
            </Router>
            </FeatureFlagsProvider>
        </ThemeProvider>
    );
};

export default App;
